import {
  SETTINGS_NAV_LABELS,
  SETTINGS_PAGE_IDS,
  SETTINGS_PATHS,
  settingsCardInSprint,
  settingsCrmIsNative,
  type SettingsPageId,
} from '@/lib/settingsPaths';

/**
 * AR-390 — the settings search at the top of the Settings column.
 *
 * Built exactly the way the Account center's is (AR-380): the index is
 * DERIVED from the pages' own field labels rather than typed twice, each entry
 * names the page it is on and the card that holds it, and a result OPENS that
 * card — the page scrolls to it and marks it for a moment.
 *
 * Two module-specific rules ride on top:
 *
 * - D-ACCT-38 — Proposal Builder and Payments + CardVault are parked. They
 *   render by link, so they are absent from `SETTINGS_PAGE_IDS` and therefore
 *   cannot be indexed at all; `visibleSettingsPages()` walks the column list.
 * - The phase and the sprint decide what a page actually shows. In Phase 1 CRM
 *   Settings is one card with the door into WorldVia PRO, so its native rows
 *   are not on the page and must not be findable; the four sprint-2 cards on
 *   AIVIA Settings are on the page but cannot be set, so they stay findable.
 */

/**
 * Every card in the module, named ONCE. The pages render `name`, the index
 * points at `focus`, and the shell scrolls to whichever card carries it — so a
 * renamed card renames its result instead of orphaning it. `focus` is the
 * card's key in the editing model where it has one (that key is what the card
 * answers to), and the card's own name where it does not.
 */
const card = (name: string, key?: string) => ({ name, focus: key ?? name });

export const SETTINGS_CARDS = {
  appearance: card('Appearance'),
  notifications: card('Notifications'),
  emailSubs: card('Email subscriptions'),
  voice: card('✦ AI writing profile', 'voice'),
  assist: card('✦ AIVIA assistance'),
  aidata: card('✦ AIVIA data + privacy'),
  // CRM Settings is one card in Phase 1 and five from Phase 2.
  crmWayIn: card('CRM'),
  crmStatus: card('CRM status'),
  crmFlow: card('Workflow defaults', 'crm'),
  crmData: card('CRM data'),
  crmTeam: card('Team access'),
  crmStyles: card('Travel styles'),
  // Cookie consent (AR-269). Not a prototype card — it is here because this
  // module's `/settings` redirect retires the legacy page that used to carry
  // it, and the control has to stay reachable.
  cookies: card('Cookies + consent'),
  sites: card('WorldVia Sites'),
  promos: card('Dynamic Promotions'),
  // The two parked pages' cards. Named here so the pages keep one source for
  // their heads; they are deliberately NOT indexed (D-ACCT-38).
  propContent: card('Default content', 'propc'),
  propTerms: card('Terms', 'propt'),
  propTermsTeam: card('Terms'),
  propClient: card('Client experience', 'propx'),
  vaultKey: card('Encryption'),
  vaultStatus: card('Vault status'),
  vaultComms: card('Communication', 'vault'),
} as const;

export type SettingsCard = (typeof SETTINGS_CARDS)[keyof typeof SETTINGS_CARDS];

export interface SettingsSearchEntry {
  /** The field's label, as the page prints it. */
  label: string;
  page: SettingsPageId;
  /** The card that holds it, as the card's head prints it. */
  card: string;
  /** What the result opens — the card's key in the editing model, or its name. */
  focus: string;
  /** Only indexed once the native CRM page replaces the Phase-1 way in. */
  nativeCrmOnly?: boolean;
}

const entry = (
  page: SettingsPageId,
  c: SettingsCard,
  labels: string[],
  opts: { nativeCrmOnly?: boolean } = {},
): SettingsSearchEntry[] =>
  labels.map((label) => ({ label, page, card: c.name, focus: c.focus, ...opts }));

export const SETTINGS_SEARCH_INDEX: readonly SettingsSearchEntry[] = [
  ...entry('theme', SETTINGS_CARDS.appearance, [
    'Color theme',
    'Display mode',
    'Text size',
  ]),

  ...entry('aivia', SETTINGS_CARDS.notifications, ['Notifications', 'Delivery rules']),
  ...entry('aivia', SETTINGS_CARDS.emailSubs, [
    'Weekly digest',
    'Promotions digest',
    'Event reminders',
  ]),
  ...entry('aivia', SETTINGS_CARDS.voice, [
    'Writing tone',
    'Communication style',
    'How AIVIA should write for you',
    'Writing samples',
  ]),
  ...entry('aivia', SETTINGS_CARDS.assist, ['AIVIA assistance']),
  ...entry('aivia', SETTINGS_CARDS.aidata, [
    'Memory',
    'Use my writing samples',
    'Export your data',
  ]),

  ...entry('crm', SETTINGS_CARDS.crmWayIn, ['CRM']),
  ...entry('crm', SETTINGS_CARDS.crmStatus, ['CRM version', 'Email connection'], {
    nativeCrmOnly: true,
  }),
  ...entry('crm', SETTINGS_CARDS.crmFlow, ['Task reminders', 'Stalled opportunities'], {
    nativeCrmOnly: true,
  }),
  ...entry('crm', SETTINGS_CARDS.crmData, ['Last CSV import'], { nativeCrmOnly: true }),
  ...entry('crm', SETTINGS_CARDS.crmTeam, ['CRM access'], { nativeCrmOnly: true }),
  ...entry('crm', SETTINGS_CARDS.crmStyles, ['Travel styles'], { nativeCrmOnly: true }),

  ...entry('aivia', SETTINGS_CARDS.cookies, ['Cookie preferences']),

  ...entry('website', SETTINGS_CARDS.sites, ['Status', 'Domain', 'Plan', 'Price']),
  ...entry('website', SETTINGS_CARDS.promos, ['Suppliers selected']),
];

/** The pages the column shows — the parked two are not among them. */
export function visibleSettingsPages(): SettingsPageId[] {
  return [...SETTINGS_PAGE_IDS];
}

export interface SettingsSearchHit extends SettingsSearchEntry {
  path: string;
  pageLabel: string;
}

/** At most eight hits, matched on the label the reader can see. */
export function searchSettings(query: string): SettingsSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const visible = new Set<SettingsPageId>(visibleSettingsPages());
  const native = settingsCrmIsNative();
  const seen = new Set<string>();
  const hits: SettingsSearchHit[] = [];
  for (const e of SETTINGS_SEARCH_INDEX) {
    if (!visible.has(e.page)) continue;
    if (e.nativeCrmOnly && !native) continue;
    // The Phase-1 way-in card is the ONLY thing on that page until the native
    // one lands, so its one entry must not show once the real rows are there.
    if (e.page === 'crm' && !e.nativeCrmOnly && native) continue;
    if (!e.label.toLowerCase().includes(q)) continue;
    const key = `${e.label}|${e.page}|${e.card}`;
    if (seen.has(key)) continue;
    seen.add(key);
    hits.push({
      ...e,
      path: SETTINGS_PATHS[e.page],
      pageLabel: SETTINGS_NAV_LABELS[e.page],
    });
    if (hits.length >= 8) break;
  }
  return hits;
}

/** Whether a card named in the index is one this sprint can actually set. */
export const settingsCardIsLive = settingsCardInSprint;
